// pages/invoices/InvoiceDetailPage.tsx - UPDATED WITH SEQUENTIAL PROOF DISPLAY

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Tooltip
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  Sync as SyncIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import axiosInstance from '../../utils/axiosConfig';

type InvoiceStatus = 'draft' | 'submitted' | 'approved' | 'paid' | 'rejected';
type ArchiveReason = 'completed' | 'cancelled' | 'expired' | 'duplicate' | 'other';

interface Invoice {
  _id: string;
  invoiceNumber: string;
  staffId: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
  };
  invoiceDate: string;
  dueDate: string;
  status: InvoiceStatus;
  totalAmount: number;
  subtotal: number;
  vatAmount: number;
  lineItems: Array<{
    itemName: string;
    description: string;
    quantity: number;
    unitPrice: number;
    companyPercentage: number;
    totalAmount: number;
    mondayEventId: string;
    imcCode: string;
    activityName?: string;
    imcCodeDescription?: string;
    proofAttachment?: {
      filename: string;
      url: string;
      uploadedAt: Date;
    };
  }>;
  createdAt: string;
  submittedAt?: string;
  pdfUrl?: string;
  notes?: string;
  mondayIntegration?: {
    items: Array<{
      itemId: string;
      itemName: string;
    }>;
  };
  mondayStatusSync?: {
    lastChecked: Date;
    mondayStatus: string;
  };
  isArchived?: boolean;
  archiveDate?: string;
  archiveReason?: ArchiveReason;
  archiveNotes?: string;
  archivedBy?: {
    _id: string;
    name: string;
    email: string;
  };
}

interface User {
  role: string;
  email: string;
}

const STATUS_COLORS: Record<InvoiceStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  draft: 'default',
  submitted: 'warning', 
  approved: 'info',
  paid: 'success',
  rejected: 'error'
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved', 
  paid: 'Paid',
  rejected: 'Rejected'
};

const ARCHIVE_REASON_LABELS: Record<ArchiveReason, string> = {
  completed: 'Completed',
  cancelled: 'Cancelled',
  expired: 'Expired',
  duplicate: 'Duplicate',
  other: 'Other'
};

const isValidStatus = (status: any): status is InvoiceStatus => {
  return ['draft', 'submitted', 'approved', 'paid', 'rejected'].includes(status);
};

const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // Action states
  const [syncing, setSyncing] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState<ArchiveReason>('completed');
  const [archiveNotes, setArchiveNotes] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('Invoice ID not provided');
        setLoading(false);
        return;
      }

      try {
        const [invoiceRes, userRes] = await Promise.all([
          axiosInstance.get(`/api/invoices/${id}`),
          axiosInstance.get('/api/users/me')
        ]);
        
        setInvoice(invoiceRes.data);
        setCurrentUser(userRes.data);
      } catch (err: any) {
        console.error('Error fetching invoice:', err);
        setError(err.response?.data?.message || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Permission checks
  const canEditInvoice = (): boolean => {
    return ['draft', 'rejected'].includes(invoice?.status ?? '') && !invoice?.isArchived && (
      currentUser?.role === 'admin' || currentUser?.role === 'gm' ||
      invoice?.staffId?.email === currentUser?.email
    );
  };

  const canDeleteInvoice = (): boolean => {
    return invoice?.status === 'draft' && !invoice?.isArchived && (
      currentUser?.role === 'admin' || currentUser?.role === 'gm' ||
      invoice?.staffId?.email === currentUser?.email
    );
  };

  const canArchiveInvoice = (): boolean => {
    return !invoice?.isArchived && invoice?.status !== 'approved' && (
      currentUser?.role === 'admin' || currentUser?.role === 'gm' ||
      invoice?.staffId?.email === currentUser?.email
    );
  };

  const canSyncInvoice = (): boolean => {
    return !!(
      invoice?.mondayIntegration?.items?.[0]?.itemId && 
      ['submitted', 'approved'].includes(invoice?.status)
    );
  };

  // Action handlers
  const handleDownload = async () => {
    if (!invoice?.pdfUrl) {
      alert('PDF not available for this invoice');
      return;
    }
    
    try {
      const response = await axiosInstance.get(`/api/invoices/${invoice._id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoiceNumber}_invoice.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice');
    }
  };

  const handleSyncStatus = async () => {
    if (!invoice) return;
    
    try {
      setSyncing(true);
      const response = await axiosInstance.post(`/api/invoices/${invoice._id}/sync-status`);
      
      setInvoice((prev: Invoice | null) => ({
        ...prev!,
        status: response.data.invoice?.status || prev!.status
      }));
      
      alert(response.data.message);
    } catch (error: any) {
      console.error('Error syncing status:', error);
      alert(error.response?.data?.message || 'Failed to sync status');
    } finally {
      setSyncing(false);
    }
  };

  const handleArchiveConfirm = async () => {
    if (!invoice) return;
    
    try {
      await axiosInstance.post(`/api/invoices/${invoice._id}/archive`, {
        reason: archiveReason,
        notes: archiveNotes
      });
      
      setInvoice((prev: Invoice | null) => ({
        ...prev!,
        isArchived: true,
        archiveDate: new Date().toISOString(),
        archiveReason,
        archiveNotes
      }));
      
      alert('Invoice archived successfully');
      setArchiveDialogOpen(false);
      setArchiveReason('completed');
      setArchiveNotes('');
      
    } catch (error: any) {
      console.error('Error archiving invoice:', error);
      alert(error.response?.data?.message || 'Failed to archive invoice');
    }
  };

  const handleUnarchive = async () => {
    if (!invoice) return;
    
    try {
      await axiosInstance.post(`/api/invoices/${invoice._id}/unarchive`);
      
      setInvoice((prev: Invoice | null) => ({
        ...prev!,
        isArchived: false,
        archiveDate: undefined,
        archiveReason: undefined,
        archiveNotes: undefined
      }));
      
      alert('Invoice unarchived successfully');
    } catch (error: any) {
      console.error('Error unarchiving invoice:', error);
      alert(error.response?.data?.message || 'Failed to unarchive invoice');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!invoice) return;
    
    try {
      await axiosInstance.delete(`/api/invoices/${invoice._id}`);
      
      alert('Invoice deleted successfully');
      setDeleteDialogOpen(false);
      navigate('/admin/invoices');
      
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      alert(error.response?.data?.message || 'Failed to delete invoice');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading invoice...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !invoice) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Invoice not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Breadcrumbs aria-label="breadcrumb" sx={{ my: 2 }}>
        <MuiLink component={Link} to="/" color="inherit">
          Home
        </MuiLink>
        <MuiLink component={Link} to="/admin/invoices" color="inherit">
          Invoices
        </MuiLink>
        <Typography color="text.primary">
          {invoice.invoiceNumber}
        </Typography>
      </Breadcrumbs>

      {/* Header with all actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/invoices')}
          >
            Back to Invoices
          </Button>
          <Typography variant="h4">
            Invoice {invoice?.invoiceNumber || 'Loading...'}
          </Typography>
          {/* Archive badge */}
          {invoice.isArchived && (
            <Chip
              icon={<ArchiveIcon />}
              label={`Archived: ${ARCHIVE_REASON_LABELS[invoice.archiveReason as ArchiveReason || 'other']}`}
              size="small"
              color="warning"
            />
          )}
        </Box>
        
        {/* Action buttons */}
        <Stack direction="row" spacing={1}>
          {/* Edit button */}
          {canEditInvoice() && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/admin/invoices/edit/${invoice._id}`)}
            >
              Edit
            </Button>
          )}

          {/* Download PDF */}
          {invoice.pdfUrl && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download PDF
            </Button>
          )}

          {/* Sync with Monday */}
          {canSyncInvoice() && (
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleSyncStatus}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Status'}
            </Button>
          )}

          {/* Archive button */}
          {canArchiveInvoice() && (
            <Button
              variant="outlined"
              startIcon={<ArchiveIcon />}
              onClick={() => setArchiveDialogOpen(true)}
              color="warning"
            >
              Archive
            </Button>
          )}

          {/* Unarchive button */}
          {invoice.isArchived && (currentUser?.role === 'admin' || currentUser?.role === 'gm') && (
            <Button
              variant="outlined"
              startIcon={<UnarchiveIcon />}
              onClick={handleUnarchive}
              color="warning"
            >
              Unarchive
            </Button>
          )}

          {/* Delete button */}
          {canDeleteInvoice() && (
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              color="error"
            >
              Delete
            </Button>
          )}

          {/* Permanent Delete (for archived invoices) */}
          {invoice.isArchived && (currentUser?.role === 'admin' || currentUser?.role === 'gm') && (
            <Button
              variant="outlined"
              startIcon={<DeleteForeverIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              color="error"
            >
              Permanently Delete
            </Button>
          )}
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Invoice Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Invoice Details
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={isValidStatus(invoice.status) ? STATUS_LABELS[invoice.status as InvoiceStatus] : invoice.status}
                  color={isValidStatus(invoice.status) ? STATUS_COLORS[invoice.status as InvoiceStatus] : 'default'}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
                <Typography>{format(new Date(invoice.invoiceDate), 'dd/MM/yyyy')}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Due Date</Typography>
                <Typography>{format(new Date(invoice.dueDate), 'dd/MM/yyyy')}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Created</Typography>
                <Typography>{format(new Date(invoice.createdAt), 'dd/MM/yyyy HH:mm')}</Typography>
              </Box>

              {invoice.submittedAt && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Submitted</Typography>
                  <Typography>{format(new Date(invoice.submittedAt), 'dd/MM/yyyy HH:mm')}</Typography>
                </Box>
              )}

              {/* Archive information */}
              {invoice.isArchived && invoice.archiveDate && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Archived</Typography>
                    <Typography>{format(new Date(invoice.archiveDate), 'dd/MM/yyyy HH:mm')}</Typography>
                  </Box>
                  {invoice.archivedBy && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Archived By</Typography>
                      <Typography>{invoice.archivedBy.name}</Typography>
                    </Box>
                  )}
                  {invoice.archiveNotes && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Archive Notes</Typography>
                      <Typography>{invoice.archiveNotes}</Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Monday Status Sync Info */}
              {invoice.mondayStatusSync && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Monday Status</Typography>
                  <Typography>{invoice.mondayStatusSync.mondayStatus}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last checked: {format(new Date(invoice.mondayStatusSync.lastChecked), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Staff Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Staff Member
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography>{invoice.staffId.name}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography>{invoice.staffId.email}</Typography>
              </Box>

              {invoice.staffId.phone && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography>{invoice.staffId.phone}</Typography>
                </Box>
              )}

              {invoice.staffId.address && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Address</Typography>
                  <Typography>
                    {invoice.staffId.address.street && <div>{invoice.staffId.address.street}</div>}
                    {(invoice.staffId.address.city || invoice.staffId.address.state) && (
                      <div>
                        {[invoice.staffId.address.city, invoice.staffId.address.state].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {invoice.staffId.address.postalCode && <div>{invoice.staffId.address.postalCode}</div>}
                    {invoice.staffId.address.country && invoice.staffId.address.country !== 'Ireland' && (
                      <div>{invoice.staffId.address.country}</div>
                    )}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Line Items
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Activity</TableCell>
                      <TableCell>IMC Code</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Company %</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="center">Proof</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.lineItems.map((item: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{item.itemName}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.activityName || `Activity ${item.mondayEventId.substring(0, 15)}...`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.imcCodeDescription || item.imcCode}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">€{item.unitPrice.toFixed(2)}</TableCell>
                        <TableCell align="right">{item.companyPercentage}%</TableCell>
                        <TableCell align="right">€{item.totalAmount.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          {item.proofAttachment ? (
                            <Tooltip title={`File: ${item.proofAttachment.filename}`}>
                              <Button
                                size="small"
                                startIcon={<ReceiptIcon />}
                                onClick={() => window.open(item.proofAttachment.url, '_blank')}
                                variant="outlined"
                                color="success"
                                sx={{ 
                                  minWidth: 'auto',
                                  fontWeight: 'bold'
                                }}
                              >
                                Proof {index + 1} {/* ⭐ CHANGED: Sequential numbering */}
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No proof
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* ⭐ NEW: Proof Summary Section */}
              {invoice.lineItems.some(item => item.proofAttachment) && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFileIcon fontSize="small" />
                    Proof Documents Summary
                  </Typography>
                  <Grid container spacing={1}>
                    {invoice.lineItems.map((item, index) => 
                      item.proofAttachment ? (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Chip
                            icon={<ReceiptIcon />}
                            label={`Proof ${index + 1}`}
                            onClick={() => window.open(item.proofAttachment!.url, '_blank')}
                            color="success"
                            variant="outlined"
                            sx={{ 
                              width: '100%',
                              justifyContent: 'flex-start',
                              '& .MuiChip-label': { 
                                fontFamily: 'monospace',
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            display: 'block', 
                            mt: 0.5, 
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            wordBreak: 'break-all'
                          }}>
                            {item.proofAttachment.filename}
                          </Typography>
                        </Grid>
                      ) : null
                    )}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Totals */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Invoice Totals
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal:</Typography>
                <Typography>€{invoice.subtotal.toFixed(2)}</Typography>
              </Box>
              
              {invoice.vatAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>VAT:</Typography>
                  <Typography>€{invoice.vatAmount.toFixed(2)}</Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 1 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6">€{invoice.totalAmount.toFixed(2)}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        {invoice.notes && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body1">
                  {invoice.notes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Archive Dialog */}
      <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)}>
        <DialogTitle>Archive Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Archive invoice {invoice?.invoiceNumber}? This will move it out of the active list.
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Archive Reason</InputLabel>
            <Select
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value as ArchiveReason)}
              label="Archive Reason"
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="duplicate">Duplicate</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Notes (optional)"
            multiline
            rows={3}
            value={archiveNotes}
            onChange={(e) => setArchiveNotes(e.target.value)}
            placeholder="Add any additional notes about why this invoice is being archived..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleArchiveConfirm} variant="contained" color="warning">
            Archive Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {invoice?.isArchived ? 'Permanently Delete Invoice' : 'Delete Invoice'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to {invoice?.isArchived ? 'permanently ' : ''}delete invoice{' '}
            <strong>{invoice?.invoiceNumber}</strong>?
          </Typography>
          {invoice?.isArchived ? (
            <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
              ⚠️ This action cannot be undone. The invoice will be permanently removed from the system.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This will permanently remove the invoice from the system.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {invoice?.isArchived ? 'Permanently Delete' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InvoiceDetailPage;